
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { mockBackend, Expense, Revenue } from '../services/mockBackend';
import { Filter, Printer, Image as ImageIcon } from 'lucide-react';
import { getLocalDateString } from '../src/lib/utils';

type ReportItemType = 'expense' | 'revenue';

interface ReportItem {
  id: string;
  itemType: ReportItemType;
  dateRef: string;
  title: string;
  supplierOrClient: string;
  category: string;
  type: string;
  status: string;
  amount: number;
  paidAmount: number | null;
  paymentDate: string | null;
  originalData: Expense | Revenue;
}

const Reports: React.FC = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<ReportItem[]>([]);
  const [filteredData, setFilteredData] = useState<ReportItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Default dates
  const date = new Date();
  const firstDay = getLocalDateString(new Date(date.getFullYear(), date.getMonth(), 1));
  const lastDay = getLocalDateString(new Date(date.getFullYear(), date.getMonth() + 1, 0));

  const [filters, setFilters] = useState({
    startDate: firstDay,
    endDate: lastDay,
    nature: 'all', // all, expense, revenue
    category: 'all',
    type: 'all',
    status: 'all'
  });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [hideRevenueTotalsInPrint, setHideRevenueTotalsInPrint] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
    setSelectedIds(new Set()); // Reset selection when filters change
  }, [items, filters]);

  const loadData = async () => {
    setIsLoading(true);
    const expensesData = await mockBackend.getExpenses(user!);
    const revenuesData = await mockBackend.getRevenues(user!);
    const cats = await mockBackend.getCategories();
    const typs = await mockBackend.getTypes();
    
    const mappedExpenses: ReportItem[] = expensesData.map(e => ({
      id: e.id,
      itemType: 'expense',
      dateRef: e.dueDate || e.createdAt.split('T')[0],
      title: e.title,
      supplierOrClient: e.supplierName,
      category: e.category,
      type: e.type,
      status: e.status, // 'open', 'paid', 'overdue'
      amount: e.amount,
      paidAmount: e.paidAmount || null,
      paymentDate: e.paymentDate || null,
      originalData: e
    }));

    const mappedRevenues: ReportItem[] = revenuesData.map(r => ({
      id: r.id,
      itemType: 'revenue',
      dateRef: r.previsao_pagamento || r.created_at.split('T')[0],
      title: r.descricao || 'Receita',
      supplierOrClient: r.cliente,
      category: r.categoria_receita || '-',
      type: r.tipo_nota || '-',
      status: r.status === 'EM_ABERTO' ? 'open' : r.status === 'RECEBIDO' ? 'paid' : r.status === 'VENCIDO' ? 'overdue' : 'canceled',
      amount: r.valor_bruto,
      paidAmount: r.valor_liquido || null,
      paymentDate: r.data_recebimento || null,
      originalData: r
    }));

    setItems([...mappedExpenses, ...mappedRevenues].sort((a, b) => a.dateRef.localeCompare(b.dateRef)));
    setCategories(cats);
    setTypes(typs);
    setIsLoading(false);
  };

  const applyFilters = () => {
    let result = [...items];

    if (filters.startDate) {
        result = result.filter(e => e.dateRef >= filters.startDate);
    }
    if (filters.endDate) {
        result = result.filter(e => e.dateRef <= filters.endDate);
    }
    
    if (filters.nature !== 'all') result = result.filter(e => e.itemType === filters.nature);
    if (filters.category !== 'all') result = result.filter(e => e.category === filters.category);
    if (filters.type !== 'all') result = result.filter(e => e.type === filters.type);
    if (filters.status !== 'all') result = result.filter(e => e.status === filters.status);

    setFilteredData(result);
  };

  const toggleSelection = (id: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedIds(newSelection);
  };

  const toggleAll = () => {
    if (selectedIds.size === filteredData.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredData.map(e => e.id)));
    }
  };

  const formatCurrency = (val: number) => val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  
  const handlePrint = () => {
    window.print();
  };
  
  // Totais
  const dataForTotals = selectedIds.size > 0 ? filteredData.filter(d => selectedIds.has(d.id)) : filteredData;
  const totalExpenses = dataForTotals.filter(d => d.itemType === 'expense').reduce((acc, curr) => acc + curr.amount, 0);
  const totalRevenues = dataForTotals.filter(d => d.itemType === 'revenue').reduce((acc, curr) => acc + curr.amount, 0);
  const totalPaidExpenses = dataForTotals.filter(d => d.itemType === 'expense').reduce((acc, curr) => acc + (curr.paidAmount || (curr.status === 'paid' ? curr.amount : 0)), 0);
  const totalReceivedRevenues = dataForTotals.filter(d => d.itemType === 'revenue').reduce((acc, curr) => acc + (curr.paidAmount || (curr.status === 'paid' ? curr.amount : 0)), 0);
  const totalQuantity = dataForTotals.reduce((acc, curr) => {
    if (curr.itemType === 'expense') {
      const exp = curr.originalData as Expense;
      return acc + (exp.quantidade !== undefined && exp.quantidade !== null ? Number(exp.quantidade) : 1);
    }
    return acc + 1;
  }, 0);
  const totalItemsCount = dataForTotals.length;
  const totalAmount = totalExpenses + (hideRevenueTotalsInPrint ? 0 : totalRevenues);

  // Load company settings
  const companyName = localStorage.getItem('company_name') || 'ENGMONT ENGENHARIA';
  const companyLegalName = localStorage.getItem('company_legal_name') || 'GEOVANI JOSE DE SOUZA EIRELI';
  const companyCnpj = localStorage.getItem('company_cnpj') || '41.906.762/0001-39';
  const companyAddress1 = localStorage.getItem('company_address1') || 'Rua Zulmira Borba, 361 - Nova Lima';
  const companyAddress2 = localStorage.getItem('company_address2') || 'Campo Grande - MS | CEP 79017-101';
  const companyEmail = localStorage.getItem('company_email') || 'geovani@engmontengenharia.com.br';
  const companyPhone = localStorage.getItem('company_phone') || '+55(67) 99609-7857';
  const reportLogoUrl = localStorage.getItem('report_logo_url') || 'https://i.ibb.co/4n731KHh/LOGO-jpeg.jpg';

  return (
    <div className="space-y-6 animate-in fade-in duration-500 font-sans">
      
      {/* Print Only Header - Aparece apenas ao imprimir */}
      <div className="hidden print:flex flex-row items-center gap-6 border-b-2 border-slate-800 pb-6 mb-8 w-full">
         <div className="w-24 h-24 shrink-0 flex items-center justify-center border border-slate-200 dark:border-slate-800 rounded bg-slate-50 dark:bg-slate-950">
             <img src={reportLogoUrl} alt={companyName} className="max-w-full max-h-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden'); }} />
             <ImageIcon size={32} className="text-slate-300 hidden" />
         </div>
         <div className="flex-1 text-left">
             <h1 className="text-xl font-bold uppercase text-slate-800 dark:text-slate-200 leading-none mb-1">{companyName}</h1>
             <h2 className="text-xs font-bold text-slate-500 uppercase mb-2">{companyLegalName}</h2>
             <div className="text-[11px] text-slate-600 dark:text-slate-400 space-y-0.5 leading-tight">
                <p><strong>CNPJ:</strong> {companyCnpj}</p>
                <p>{companyAddress1}</p>
                <p>{companyAddress2}</p>
                <p>{companyEmail} | {companyPhone}</p>
             </div>
         </div>
         <div className="text-right">
             <h1 className="text-xl font-bold text-slate-800 tracking-tight">RELATÓRIO FINANCEIRO</h1>
             <p className="text-sm text-slate-500 mt-1">Gerado em: {new Date().toLocaleDateString()}</p>
         </div>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 print:hidden">
        <div>
           <h2 className="text-xl font-bold text-slate-800">Relatórios Financeiros</h2>
           <p className="text-sm text-slate-500">Exporte dados para contabilidade ou análise.</p>
        </div>
        <div className="flex gap-2">
           <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 mr-4">
             <input type="checkbox" checked={hideRevenueTotalsInPrint} onChange={e => setHideRevenueTotalsInPrint(e.target.checked)} className="rounded border-slate-300" />
             Ocultar Receitas no PDF
           </label>
           <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm font-medium">
              <Printer size={16} /> Imprimir
           </button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 p-4 rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm print:hidden">
         <div className="flex items-center gap-2 mb-4 text-slate-700 dark:text-slate-300 font-bold text-sm border-b border-slate-100 dark:border-slate-800 pb-2">
            <Filter size={16} /> Filtros
         </div>
         <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div>
               <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Data Inicial</label>
               <input type="date" value={filters.startDate} onChange={e => setFilters({...filters, startDate: e.target.value})} className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200" />
            </div>
            <div>
               <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Data Final</label>
               <input type="date" value={filters.endDate} onChange={e => setFilters({...filters, endDate: e.target.value})} className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200" />
            </div>
            <div>
               <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Natureza</label>
               <select value={filters.nature} onChange={e => setFilters({...filters, nature: e.target.value})} className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200">
                  <option value="all">Todas</option>
                  <option value="expense">Despesas</option>
                  <option value="revenue">Receitas</option>
               </select>
            </div>
            <div>
               <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Tipo</label>
               <select value={filters.type} onChange={e => setFilters({...filters, type: e.target.value})} className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200">
                  <option value="all">Todos</option>
                  {types.map(t => <option key={t} value={t}>{t}</option>)}
               </select>
            </div>
            <div>
               <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Categoria</label>
               <select value={filters.category} onChange={e => setFilters({...filters, category: e.target.value})} className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200">
                  <option value="all">Todas</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
               </select>
            </div>
             <div>
               <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Status</label>
               <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})} className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200">
                  <option value="all">Todos</option>
                  <option value="open">Aberto</option>
                  <option value="paid">Pago/Recebido</option>
                  <option value="overdue">Vencido</option>
               </select>
            </div>
         </div>
      </div>

      {/* Info de Filtro Aplicado (Aparece apenas na impressão para contexto) */}
      <div className="hidden print:block mb-4 border-b border-slate-200 pb-2">
         <p className="text-xs text-slate-500">
            <strong>Filtro aplicado:</strong> De {new Date(filters.startDate.includes('T') ? filters.startDate : `${filters.startDate}T12:00:00`).toLocaleDateString()} até {new Date(filters.endDate.includes('T') ? filters.endDate : `${filters.endDate}T12:00:00`).toLocaleDateString()} 
            {filters.nature !== 'all' && ` | Natureza: ${filters.nature === 'expense' ? 'Despesas' : 'Receitas'}`}
            {filters.type !== 'all' && ` | Tipo: ${filters.type}`}
            {filters.category !== 'all' && ` | Categoria: ${filters.category}`}
            {filters.status !== 'all' && ` | Status: ${filters.status === 'open' ? 'Aberto' : filters.status === 'paid' ? 'Pago/Recebido' : 'Vencido'}`}
         </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:hidden">
          <div className="bg-white dark:bg-slate-900 p-4 print:p-2 rounded border border-slate-200 dark:border-slate-800 print:border-slate-300">
             <span className="text-xs print:text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase">Total Despesas</span>
             <div className="text-xl print:text-sm font-bold text-red-600 dark:text-red-500">{formatCurrency(totalExpenses)}</div>
          </div>
          <div className="bg-white dark:bg-slate-900 p-4 print:p-2 rounded border border-slate-200 dark:border-slate-800 print:border-slate-300">
             <span className="text-xs print:text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase">Despesas Pagas</span>
             <div className="text-xl print:text-sm font-bold text-slate-800 dark:text-slate-200">{formatCurrency(totalPaidExpenses)}</div>
          </div>
          <div className={`bg-white dark:bg-slate-900 p-4 print:p-2 rounded border border-slate-200 dark:border-slate-800 print:border-slate-300 ${hideRevenueTotalsInPrint ? 'print:hidden' : ''}`}>
             <span className="text-xs print:text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase">Total Receitas</span>
             <div className="text-xl print:text-sm font-bold text-green-600 dark:text-green-500">{formatCurrency(totalRevenues)}</div>
          </div>
          <div className={`bg-white dark:bg-slate-900 p-4 print:p-2 rounded border border-slate-200 dark:border-slate-800 print:border-slate-300 ${hideRevenueTotalsInPrint ? 'print:hidden' : ''}`}>
             <span className="text-xs print:text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase">Receitas Recebidas</span>
             <div className="text-xl print:text-sm font-bold text-slate-800 dark:text-slate-200">{formatCurrency(totalReceivedRevenues)}</div>
          </div>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm overflow-hidden print:overflow-visible print:shadow-none print:border-none">
         <div className="overflow-x-auto print:overflow-visible">
         <table className="w-full text-left border-collapse">
            <thead>
               <tr className="bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 text-xs print:text-[10px] uppercase border-b border-slate-200 dark:border-slate-800 print:bg-slate-100 print:text-black">
                  <th className="px-4 py-3 print:px-2 print:py-1 w-10 print:hidden">
                     <input 
                       type="checkbox" 
                       checked={filteredData.length > 0 && selectedIds.size === filteredData.length}
                       onChange={toggleAll}
                       className="rounded border-slate-300 text-slate-600 focus:ring-slate-500"
                     />
                  </th>
                  <th className="px-4 py-3 print:px-2 print:py-1">Natureza</th>
                  <th className="px-4 py-3 print:px-2 print:py-1">Data Ref.</th>
                  <th className="px-4 py-3 print:px-2 print:py-1">Título</th>
                  <th className="px-4 py-3 print:px-2 print:py-1">Tipo</th>
                  <th className="px-4 py-3 print:px-2 print:py-1">Categoria</th>
                  <th className="px-4 py-3 print:px-2 print:py-1">Status</th>
                  <th className="px-4 py-3 print:px-2 print:py-1 text-center">Qtd</th>
                  <th className="px-4 py-3 print:px-2 print:py-1 text-right">Valor</th>
               </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50 text-sm print:text-[10px] print:divide-slate-300">
               {filteredData.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-8 text-slate-400 dark:text-slate-500">Nenhum registro encontrado.</td></tr>
               ) : (
                  filteredData.map(exp => (
                     <tr key={exp.id} className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 print:break-inside-avoid ${selectedIds.size > 0 && !selectedIds.has(exp.id) ? 'print:hidden' : ''}`}>
                        <td className="px-4 py-2 print:px-2 print:py-1 print:hidden">
                           <input 
                             type="checkbox" 
                             checked={selectedIds.has(exp.id)}
                             onChange={() => toggleSelection(exp.id)}
                             className="rounded border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 focus:ring-slate-500 dark:bg-slate-800"
                           />
                        </td>
                        <td className="px-4 py-2 print:px-2 print:py-1">
                           {exp.itemType === 'expense' ? (
                              <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-xs print:text-[9px] font-medium border border-red-100">Despesa</span>
                           ) : (
                              <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs print:text-[9px] font-medium border border-green-100">Receita</span>
                           )}
                        </td>
                        <td className="px-4 py-2 print:px-2 print:py-1 text-slate-500 dark:text-slate-400 font-mono text-xs print:text-[10px]">{new Date(exp.dateRef.includes('T') ? exp.dateRef : `${exp.dateRef}T12:00:00`).toLocaleDateString()}</td>
                        <td className="px-4 py-2 print:px-2 print:py-1">
                           <div className="font-bold text-slate-700 dark:text-slate-200 print:text-[10px]">{exp.title}</div>
                           <div className="text-[10px] print:text-[8px] text-slate-400 dark:text-slate-500">{exp.supplierOrClient}</div>
                        </td>
                        <td className="px-4 py-2 print:px-2 print:py-1"><span className="px-2 py-0.5 bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 rounded text-xs print:text-[9px] font-medium border border-blue-100 dark:border-blue-500/20 print:border-slate-300">{exp.type}</span></td>
                        <td className="px-4 py-2 print:px-2 print:py-1"><span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded text-xs print:text-[9px] border border-transparent dark:border-slate-700 print:bg-transparent print:border print:border-slate-300">{exp.category}</span></td>
                        <td className="px-4 py-2 print:px-2 print:py-1">
                           {exp.status === 'paid' ? <span className="text-green-600 dark:text-green-400 font-bold text-xs print:text-[9px] uppercase">{exp.itemType === 'expense' ? 'Pago' : 'Recebido'}</span> : 
                            exp.status === 'overdue' ? <span className="text-red-600 dark:text-red-400 font-bold text-xs print:text-[9px] uppercase">Vencido</span> :
                            <span className="text-blue-600 dark:text-blue-400 font-bold text-xs print:text-[9px] uppercase">Aberto</span>}
                        </td>
                        <td className="px-4 py-2 print:px-2 print:py-1 text-center font-mono text-xs print:text-[10px]">
                           {exp.itemType === 'expense' ? ((exp.originalData as Expense).quantidade ?? 1) : 1}
                        </td>
                        <td className="px-4 py-2 print:px-2 print:py-1 text-right font-mono text-slate-800 dark:text-slate-200 print:text-[10px]">
                           {formatCurrency(exp.amount)}
                        </td>
                     </tr>
                  ))
               )}
            </tbody>
            <tfoot className="border-t-2 border-slate-800 bg-slate-100 dark:bg-slate-800/80 font-bold text-slate-800 dark:text-slate-100 print:bg-slate-100 print:border-slate-800">
               <tr className="text-sm print:text-[10px]">
                  <td className="px-4 py-2.5 print:px-2 print:py-1.5 print:hidden"></td>
                  <td colSpan={6} className="px-4 py-2.5 print:px-2 print:py-1.5 font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                     Total Geral ({totalItemsCount} {totalItemsCount === 1 ? 'registro' : 'registros'})
                  </td>
                  <td className="px-4 py-2.5 print:px-2 print:py-1.5 text-center font-mono font-bold text-slate-900 dark:text-slate-100">
                     {totalQuantity}
                  </td>
                  <td className="px-4 py-2.5 print:px-2 print:py-1.5 text-right font-mono text-base print:text-[11px] font-bold text-slate-900 dark:text-slate-100">
                     {formatCurrency(totalAmount)}
                  </td>
               </tr>
            </tfoot>
         </table>
         </div>
      </div>
    </div>
  );
};

export default Reports;
